import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardBody, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Button, Spinner } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { withTranslation } from 'react-i18next';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import useQsParams from '../../Components/Hooks/QueryString';
import { getWalletSummary } from '../../slices/wallet-summary/thunk';
import { getAccountTransactions } from '../../slices/account-transaction/thunk';
import { DATE_PRESET_KEYS } from './action';
import WalletMetrics from './components/WalletMetrics';
import ProximityBar from './components/ProximityBar';
import WalletLedger from './components/WalletLedger';
import moment from 'moment';

// Selectors defined outside component for proper memoization
const selectWalletSummary = (state) => state.WalletSummary.summary;
const WalletSummaryState = createSelector(
  [selectWalletSummary],
  (summary) => summary
);

const selectWalletLoading = (state) => state.WalletSummary.isLoading;
const WalletSummaryLoadingState = createSelector(
  [selectWalletLoading],
  (isLoading) => isLoading
);

const selectAccountTransactions = (state) => state.AccountTransaction.accountTransactions;
const AccountTransactionState = createSelector(
  [selectAccountTransactions],
  (accountTransactions) => accountTransactions
);

const selectTransactionsLoading = (state) => state.AccountTransaction.isLoading;
const loadingState = createSelector(
  [selectTransactionsLoading],
  (isLoading) => isLoading
);

const selectMeta = (state) => state.AccountTransaction.meta;
const metaState = createSelector(
  [selectMeta],
  (meta) => meta
);

const WalletOverview = (props) => {
  const dispatch = useDispatch();
  const { searchParams, setSearchParams } = useQsParams();
  const [datePresetOpen, setDatePresetOpen] = useState(false);

  const authUser = JSON.parse(sessionStorage.getItem('authUser') ?? '{}');
  const accountName = authUser?.account?.name || authUser?.account_name || '-';
  const accountActive = authUser?.account?.active ?? true;

  // Date preset from URL, default to this_month
  const presetFromUrl = searchParams.get('datePreset') || 'this_month';
  const [selectedPreset, setSelectedPreset] = useState(presetFromUrl);

  // Compute date range from preset
  const getDateRange = (preset) => {
    const now = moment();
    let dateFrom, dateTo;

    switch (preset) {
      case 'this_month':
        dateFrom = moment().startOf('month');
        dateTo = now;
        break;
      case 'last_month':
        dateFrom = moment().subtract(1, 'month').startOf('month');
        dateTo = moment().subtract(1, 'month').endOf('month');
        break;
      case 'last_15_days':
        dateFrom = now.clone().subtract(15, 'days');
        dateTo = now;
        break;
      case 'last_7_days':
        dateFrom = now.clone().subtract(7, 'days');
        dateTo = now;
        break;
      default:
        dateFrom = moment().startOf('month');
        dateTo = now;
    }

    return {
      dateFrom: dateFrom.toISOString(),
      dateTo: dateTo.toISOString(),
    };
  };

  const { dateFrom, dateTo } = useMemo(
    () => getDateRange(selectedPreset),
    [selectedPreset]
  );

  const summary = useSelector(WalletSummaryState);
  const summaryIsLoading = useSelector(WalletSummaryLoadingState);

  const accountTransactions = useSelector(AccountTransactionState);
  const transactionsIsLoading = useSelector(loadingState);
  const meta = useSelector(metaState);

  // Extract page value before useEffect to avoid function call in dependency array
  const currentPage = searchParams.get('page');

  // Fetch data on mount and when preset changes
  useEffect(() => {
    dispatch(getWalletSummary({ dateFrom, dateTo }));
    dispatch(
      getAccountTransactions({
        page: currentPage ?? 1,
        filters: {
          created_at: {
            between_date: [dateFrom, dateTo],
          },
        },
      })
    );
  }, [dispatch, dateFrom, dateTo, currentPage]);

  // Sync URL preset with state
  useEffect(() => {
    const urlPreset = searchParams.get('datePreset');
    if (urlPreset && urlPreset !== selectedPreset) {
      setSelectedPreset(urlPreset);
    }
  }, [searchParams.get('datePreset')]);

  // Set document title as a side effect
  useEffect(() => {
    document.title = `${props.t('wallet-overview.heading')} | OCK`;
  }, [props.t]);

  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    setSearchParams('datePreset', preset);
    setSearchParams('page', 1);
    setDatePresetOpen(false);
  };

  const isLoading = summaryIsLoading || transactionsIsLoading;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={props.t('wallet-overview.heading')}
          pageTitle={props.t('ockcash')}
        />

        {/* Top bar: Account info + Date preset selector */}
        <Card className="mb-3">
          <CardBody>
            <Row className="align-items-center">
              <Col xs={12} md={6}>
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center"
                    style={{ width: '40px', height: '40px' }}
                  >
                    <i className="bx bx-building-house text-primary" style={{ fontSize: '1.2rem' }}></i>
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">{accountName}</h6>
                    <span
                      className={`badge ${accountActive ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}
                      style={{ fontSize: '0.7rem' }}
                    >
                      {accountActive
                        ? props.t('wallet-overview.status.active')
                        : props.t('wallet-overview.status.inactive')}
                    </span>
                  </div>
                </div>
              </Col>
              <Col xs={12} md={6} className="text-md-end mt-2 mt-md-0">
                <div className="d-inline-block">
                  <Dropdown isOpen={datePresetOpen} toggle={() => setDatePresetOpen(!datePresetOpen)}>
                    <DropdownToggle
                      tag="button"
                      className="btn btn-soft-primary dropdown-toggle"
                      onClick={() => setDatePresetOpen(!datePresetOpen)}
                    >
                      <i className="bx bx-calendar me-1"></i>
                      {props.t(`wallet-overview.date-presets.${selectedPreset}`)}
                    </DropdownToggle>
                    <DropdownMenu isOpen={datePresetOpen}>
                      {DATE_PRESET_KEYS.map((key) => (
                        <DropdownItem
                          key={key}
                          onClick={() => handlePresetChange(key)}
                          className={selectedPreset === key ? 'active' : ''}
                        >
                          {props.t(`wallet-overview.date-presets.${key}`)}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Metrics Cards */}
        <WalletMetrics summary={summary} isLoading={isLoading} />

        {/* Proximity Bar */}
        <ProximityBar summary={summary} />

        {/* Ledger Table */}
        <WalletLedger
          transactions={accountTransactions}
          isLoading={transactionsIsLoading}
          meta={meta}
          page={searchParams.get('page') ?? 1}
          setPage={(p) => setSearchParams('page', p)}
        />
      </Container>
    </div>
  );
};

export default withTranslation()(WalletOverview);
